'use client'

import React from 'react'

import {
  ArrowUpRight,
  Briefcase,
  Building2,
  FileText,
  Info,
  MapPin,
  Tag,
} from 'lucide-react'
import Link from 'next/link'

import PopoverV2 from '@/components/common/PopoverV2'

import { formatSubawardType } from '@/lib/usaspending/awardTypeMappings'

import type { SubAward } from '@/types/usaspending'

import {
  formatBusinessType,
  formatLocationString,
  normalizePlaceOfPerformance,
} from '../../common/transforms'
import { getCategoryConfig } from './categoryConfig'

// ============================================================================
// Helpers: Format locations
// ============================================================================

function formatRecipientLocation(
  location: SubAward['subAwardee']['location'] | undefined,
): string | null {
  if (!location) return null

  const parts: string[] = []
  if (location.city) parts.push(location.city)
  if (location.state) parts.push(location.state)

  return parts.length > 0 ? parts.join(', ') : null
}

// ============================================================================
// SubAwardPreviewPopover Component
// ============================================================================

interface SubAwardPreviewPopoverProps {
  /** The subaward data to display */
  subAward: SubAward
  /** The trigger element (subaward number display) */
  children: React.ReactNode
  /** Award category for category-aware terminology */
  category?: string | null
}

/**
 * Click-to-open popover for subaward quick preview.
 * Shows ADDITIONAL information not already visible in the table row:
 * - Recipient location (city, state)
 * - Place of performance (if different)
 * - Recipient UEI
 * - Business types
 * - Subaward type (subcontract/subgrant)
 * - NAICS code/description
 * - Description (if meaningful)
 *
 * Follows compositional patterns:
 * - Pure presentation component
 * - Uses PopoverV2 compound component
 * - Styled to match award page card components
 */
export const SubAwardPreviewPopover: React.FC<SubAwardPreviewPopoverProps> = ({
  subAward,
  children,
  category,
}) => {
  const categoryConfig = getCategoryConfig(category)
  const subawardPageUrl = subAward.subawardId
    ? `/awards/subaward/${encodeURIComponent(subAward.subawardId)}`
    : null

  // Extract additional recipient details not shown in table
  const recipientLocation = formatRecipientLocation(
    subAward.subAwardee?.location,
  )
  // Use shared PoP normalizer for consistent formatting
  const pop = normalizePlaceOfPerformance(subAward.placeOfPerformance)
  const recipientUei = subAward.subAwardee?.uei
  const businessTypes = subAward.subAwardee?.businessTypes ?? []
  const subawardType = formatSubawardType(subAward.subawardType)
  const naicsCode = subAward.naicsCode
  const naicsDescription = subAward.naicsDescription

  // Check if place of performance is different from business location
  const showPlaceOfPerformance =
    pop.formatted && pop.formatted !== recipientLocation

  // Check if we have a description to show
  const hasDescription = !!subAward.description?.trim()

  // Check if we have any additional info to show
  const hasAdditionalInfo =
    recipientLocation ||
    showPlaceOfPerformance ||
    recipientUei ||
    businessTypes.length > 0 ||
    subawardType ||
    naicsCode ||
    hasDescription

  // If no additional info, don't render a popover
  if (!hasAdditionalInfo && !subawardPageUrl) {
    return <>{children}</>
  }

  return (
    <PopoverV2>
      <PopoverV2.Trigger asChild>{children}</PopoverV2.Trigger>
      <PopoverV2.Content
        className="w-72 p-0"
        side="right"
        align="start"
        sideOffset={8}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-primary-100 px-3 py-2 dark:border-primary-800">
          <span className="text-xs font-semibold text-primary-900 dark:text-primary-100">
            {categoryConfig.subawardeeDetailsTitle}
          </span>
          {subawardPageUrl && (
            <Link
              href={subawardPageUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-1 text-xs font-medium text-accent-600 hover:text-blue-600 hover:underline dark:text-accent-400 dark:hover:text-blue-400"
            >
              Full Details
              <ArrowUpRight className="h-3 w-3 opacity-70 transition-opacity group-hover:opacity-100" />
            </Link>
          )}
        </div>

        {/* Content - Only shows info NOT already in table */}
        <div className="space-y-2.5 p-3">
          {/* Subaward Type Badge */}
          {subawardType && (
            <div className="flex items-center gap-2">
              <FileText className="h-3.5 w-3.5 flex-shrink-0 text-primary-400" />
              <span className="rounded-full border border-accent-200 bg-accent-50 px-2 py-0.5 text-xs font-semibold text-accent-700 dark:border-accent-700 dark:bg-accent-900/30 dark:text-accent-300">
                {subawardType}
              </span>
            </div>
          )}

          {/* Recipient Location */}
          {recipientLocation && (
            <div className="flex items-start gap-2">
              <Building2 className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-primary-400" />
              <div>
                <span className="text-xs font-semibold uppercase tracking-wider text-primary-400">
                  Business Location
                </span>
                <div className="text-xs font-medium text-primary-900 dark:text-primary-100">
                  {recipientLocation}
                </div>
              </div>
            </div>
          )}

          {/* Place of Performance (if different from business location) */}
          {showPlaceOfPerformance && (
            <div className="flex items-start gap-2">
              <MapPin className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-primary-400" />
              <div>
                <span className="text-xs font-semibold uppercase tracking-wider text-primary-400">
                  Place of Performance
                </span>
                <div className="text-xs font-medium text-primary-900 dark:text-primary-100">
                  {pop.formatted}
                </div>
                {/* County for domestic locations */}
                {pop.county && !pop.isForeign && (
                  <div className="text-xs text-primary-500 dark:text-primary-200">
                    {pop.county} County
                  </div>
                )}
                {/* Province for foreign locations */}
                {pop.foreignProvince && pop.isForeign && (
                  <div className="text-xs text-primary-500 dark:text-primary-200">
                    Province: {pop.foreignProvince}
                  </div>
                )}
                {/* ZIP code (pre-formatted with +4) */}
                {pop.zipFormatted && (
                  <div className="text-xs text-primary-500 dark:text-primary-200">
                    ZIP: {pop.zipFormatted}
                  </div>
                )}
                {/* Congressional district (pre-formatted with redistricting info) */}
                {pop.congressionalDistrictFormatted && (
                  <div className="text-xs text-primary-500 dark:text-primary-200">
                    CD: {pop.congressionalDistrictFormatted}
                    {pop.hasRedistricted && pop.congressionalDistrict && (
                      <span className="ml-1 text-primary-400 dark:text-primary-500">
                        (was {pop.congressionalDistrict})
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Recipient UEI */}
          {recipientUei && (
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-primary-400">
                UEI
              </span>
              <div className="font-mono text-xs text-primary-700 dark:text-primary-300">
                {recipientUei}
              </div>
            </div>
          )}

          {/* NAICS Code */}
          {naicsCode && (
            <div className="flex items-start gap-2">
              <Tag className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-primary-400" />
              <div>
                <span className="text-xs font-semibold uppercase tracking-wider text-primary-400">
                  NAICS
                </span>
                <div className="text-xs font-medium text-primary-900 dark:text-primary-100">
                  {naicsCode}
                </div>
                {naicsDescription && (
                  <div className="text-xs text-primary-500 dark:text-primary-200">
                    {naicsDescription}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Business Types */}
          {businessTypes.length > 0 && (
            <div>
              <span className="mb-1 flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-primary-400">
                <Briefcase className="h-3 w-3" />
                Business Categories
              </span>
              <div className="flex flex-wrap gap-1">
                {businessTypes.slice(0, 4).map((type) => (
                  <span
                    key={type}
                    className="rounded border border-primary-200 bg-primary-100 px-1.5 py-0.5 text-xs font-medium text-primary-600 dark:border-primary-700 dark:bg-primary-800 dark:text-primary-200"
                  >
                    {formatBusinessType(type)}
                  </span>
                ))}
                {businessTypes.length > 4 && (
                  <span className="px-1 text-xs text-primary-400">
                    +{businessTypes.length - 4} more
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Description */}
          {hasDescription && (
            <div className="rounded-md border border-primary-100 bg-primary-50 p-2 dark:border-primary-800 dark:bg-primary-800/30">
              <span className="mb-0.5 block text-xs font-semibold uppercase tracking-wider text-primary-400">
                Description
              </span>
              <p className="line-clamp-3 text-xs leading-relaxed text-primary-600 dark:text-primary-200">
                {subAward.description}
              </p>
            </div>
          )}
        </div>
      </PopoverV2.Content>
    </PopoverV2>
  )
}

// ============================================================================
// SubAwardPreviewTrigger Component
// ============================================================================

interface SubAwardPreviewTriggerProps {
  /** The subaward number to display */
  subawardNumber: string | null
  /** Whether this subaward has additional details to preview */
  hasPreview: boolean
}

/**
 * Trigger button for the subaward preview popover.
 * Shows the subaward number with an info icon indicating click-to-preview.
 */
export const SubAwardPreviewTrigger = React.forwardRef<
  HTMLButtonElement,
  SubAwardPreviewTriggerProps
>(({ subawardNumber, hasPreview, ...props }, ref) => {
  return (
    <button
      ref={ref}
      type="button"
      className={`group inline-flex items-center gap-1.5 whitespace-nowrap font-mono text-xs ${
        hasPreview
          ? 'cursor-pointer text-accent-600 hover:text-blue-600 dark:text-accent-400 dark:hover:text-blue-400'
          : 'cursor-default text-primary-600 dark:text-primary-200'
      }`}
      {...props}
    >
      {subawardNumber || '—'}
      {hasPreview && (
        <Info className="h-3.5 w-3.5 rounded-full opacity-60 transition-opacity group-hover:opacity-100" />
      )}
    </button>
  )
})

SubAwardPreviewTrigger.displayName = 'SubAwardPreviewTrigger'

export type { SubAwardPreviewPopoverProps, SubAwardPreviewTriggerProps }
